import React, { useState } from "react";
import { api } from "@/lib/api";
import { ui, GoldButton } from "@/components/Primitives";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, FileSpreadsheet, FileType, Download, Loader2 } from "lucide-react";
import { DisclaimerFooter } from "@/components/Disclaimer";

const FORMATS = [
  { key: "pdf", label: "PDF", icon: FileText, hint: "Print-ready" },
  { key: "xlsx", label: "Excel", icon: FileSpreadsheet, hint: ".xlsx" },
  { key: "docx", label: "Word", icon: FileType, hint: ".docx" },
];
const PERIODS = [
  { key: "month", label: "This Month" },
  { key: "quarter", label: "This Quarter" },
  { key: "fy", label: "Financial Year" },
  { key: "custom", label: "Custom" },
];

export default function ExportDialog({ open, onClose }) {
  const [fmt, setFmt] = useState("pdf");
  const [period, setPeriod] = useState("month");
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [loading, setLoading] = useState(false);

  const run = async () => {
    if (period === "custom" && (!start || !end)) return toast.error("Choose a start and end date");
    setLoading(true);
    try {
      const res = await api.get("/export", {
        params: { fmt, period, start, end }, responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `finetomi-cashflow-${period}.${fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Report downloaded");
      onClose();
    } catch {
      toast.error("Could not generate report");
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className={ui.dialog}>
        <DialogHeader>
          <DialogTitle className="font-display text-2xl font-light">Export Centre</DialogTitle>
          <DialogDescription className="text-[#8A7F70]">Professional reports for your accountant, broker or records.</DialogDescription>
        </DialogHeader>
        <div className="space-y-5 py-2">
          <div>
            <Label className={ui.label}>Format</Label>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {FORMATS.map((f) => (
                <button key={f.key} data-testid={`export-fmt-${f.key}`} onClick={() => setFmt(f.key)}
                  className={`p-3 rounded-xl border text-center transition-all ${fmt === f.key ? "border-[#C6A45C] bg-[#C6A45C]/10" : "border-[#EAE0D2] bg-white/60 hover:border-[#D8BE83]"}`}>
                  <f.icon size={20} className="mx-auto text-[#C6A45C]" strokeWidth={1.5} />
                  <div className="text-xs mt-1.5 text-[#2C2A28]">{f.label}</div>
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className={ui.label}>Period</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {PERIODS.map((p) => (
                <button key={p.key} data-testid={`export-period-${p.key}`} onClick={() => setPeriod(p.key)}
                  className={`py-2.5 rounded-xl border text-sm transition-all ${period === p.key ? "border-[#C6A45C] bg-[#C6A45C]/10 text-[#8a6d2f]" : "border-[#EAE0D2] bg-white/60 text-[#6b6256] hover:border-[#D8BE83]"}`}>
                  {p.label}
                </button>
              ))}
            </div>
          </div>
          {period === "custom" && (
            <div className="grid grid-cols-2 gap-3">
              <div><Label className={ui.label}>From</Label><Input data-testid="export-start" type="date" value={start} onChange={(e) => setStart(e.target.value)} className={ui.input} /></div>
              <div><Label className={ui.label}>To</Label><Input data-testid="export-end" type="date" value={end} onChange={(e) => setEnd(e.target.value)} className={ui.input} /></div>
            </div>
          )}
        </div>
        <DialogFooter>
          <GoldButton data-testid="export-run" onClick={run} disabled={loading} className="w-full">
            {loading ? <Loader2 size={16} className="animate-spin" /> : <><Download size={16} /> Download report</>}
          </GoldButton>
        </DialogFooter>
        <DisclaimerFooter className="mt-2 text-center" />
      </DialogContent>
    </Dialog>
  );
}
